import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            {/* Logo with Enhanced Caption */}
            <div className="flex items-center gap-4">
              <img 
                src="https://static.readdy.ai/image/7f238032a05683993a97bc96465f66ed/a554bab3c45cd373e1d11bc666f4c0d1.png" 
                alt="DJT DRONE AIRCRAFT" 
                className={`w-auto object-contain transition-all duration-500 ${isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20 lg:h-24'}`}
              />
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1 overflow-hidden">
                  {['E', 'V', ' ', 'D', 'R', 'O', 'N', 'E'].map((letter, index) => (
                    <span
                      key={`ev-${index}`}
                      className={`font-bold tracking-wider transition-all duration-500 logo-letter ${
                        isScrolled ? 'text-gray-900 text-sm md:text-base' : 'text-white text-base md:text-lg'
                      }`}
                      style={{
                        animation: 'slideInBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
                        animationDelay: `${index * 0.08}s`,
                        opacity: 0,
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1 overflow-hidden">
                  {['A', 'I', 'R', 'C', 'R', 'A', 'F', 'T'].map((letter, index) => (
                    <span
                      key={`aircraft-${index}`}
                      className={`font-bold tracking-wider transition-all duration-500 logo-letter-glow ${
                        isScrolled ? 'text-teal-500 text-sm md:text-base' : 'text-teal-400 text-base md:text-lg'
                      }`}
                      style={{
                        animation: 'slideInRotate 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
                        animationDelay: `${index * 0.08 + 0.3}s`,
                        opacity: 0,
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <nav className="hidden lg:flex items-center space-x-8">
              <a href="#aircraft" onClick={(e) => handleNavClick(e, '#aircraft')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Aircraft
              </a>
              <a href="#solutions" onClick={(e) => handleNavClick(e, '#solutions')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Solutions
              </a>
              <a href="#technology" onClick={(e) => handleNavClick(e, '#technology')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Technology
              </a>
              <a href="#news" onClick={(e) => handleNavClick(e, '#news')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                News
              </a>
              <a href="#contact" onClick={(e) => handleNavClick(e, '#contact')} className={`text-sm font-medium transition-colors hover:text-teal-500 cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Contact
              </a>
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <a href="tel:1-800-570-0229" className={`px-6 py-2 text-sm font-medium rounded-full transition-all flex items-center space-x-2 ${isScrolled ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-white text-gray-900 hover:bg-gray-100'}`}>
                <i className="ri-phone-line"></i>
                <span>1-800-570-0229</span>
              </a>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2"
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-white lg:hidden">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-6 border-b">
              {/* Mobile Logo with Enhanced Caption */}
              <div className="flex items-center gap-3">
                <img 
                  src="https://static.readdy.ai/image/7f238032a05683993a97bc96465f66ed/a554bab3c45cd373e1d11bc666f4c0d1.png" 
                  alt="DJT DRONE AIRCRAFT" 
                  className="h-16 md:h-20"
                />
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    {['E', 'V', ' ', 'D', 'R', 'O', 'N', 'E'].map((letter, index) => (
                      <span
                        key={`mobile-ev-${index}`}
                        className="font-bold tracking-wider text-gray-900 text-sm md:text-base logo-letter"
                        style={{
                          animation: 'slideInBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
                          animationDelay: `${index * 0.08}s`,
                          opacity: 0,
                        }}
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    {['A', 'I', 'R', 'C', 'R', 'A', 'F', 'T'].map((letter, index) => (
                      <span
                        key={`mobile-aircraft-${index}`}
                        className="font-bold tracking-wider text-teal-500 text-sm md:text-base logo-letter-glow"
                        style={{
                          animation: 'slideInRotate 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
                          animationDelay: `${index * 0.08 + 0.3}s`,
                          opacity: 0,
                        }}
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <i className="ri-close-line text-2xl text-gray-900"></i>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <a href="#aircraft" onClick={(e) => handleNavClick(e, '#aircraft')} className="block text-lg font-medium text-gray-900 hover:text-teal-500 cursor-pointer">
                  Aircraft
                </a>
                <a href="#solutions" onClick={(e) => handleNavClick(e, '#solutions')} className="block text-lg font-medium text-gray-900 hover:text-teal-500 cursor-pointer">
                  Solutions
                </a>
                <a href="#technology" onClick={(e) => handleNavClick(e, '#technology')} className="block text-lg font-medium text-gray-900 hover:text-teal-500 cursor-pointer">
                  Technology
                </a>
                <a href="#news" onClick={(e) => handleNavClick(e, '#news')} className="block text-lg font-medium text-gray-900 hover:text-teal-500 cursor-pointer">
                  News
                </a>
                <a href="#contact" onClick={(e) => handleNavClick(e, '#contact')} className="block text-lg font-medium text-gray-900 hover:text-teal-500 cursor-pointer">
                  Contact
                </a>
                <a href="tel:1-800-570-0229" className="block text-lg font-medium text-teal-500 hover:text-teal-600 flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>1-800-570-0229</span>
                </a>
              </div>
            </nav>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInBounce {
          0% {
            opacity: 0;
            transform: translateX(-40px) scale(0.5);
          }
          60% {
            opacity: 1;
            transform: translateX(5px) scale(1.1);
          }
          80% {
            transform: translateX(-2px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes slideInRotate {
          0% {
            opacity: 0;
            transform: translateY(-30px) rotate(-15deg) scale(0.5);
          }
          60% {
            opacity: 1;
            transform: translateY(3px) rotate(5deg) scale(1.15);
          }
          80% {
            transform: translateY(-1px) rotate(-2deg) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) rotate(0deg) scale(1);
          }
        }

        .logo-letter {
          display: inline-block;
          transition: all 0.3s ease;
        }

        .logo-letter:hover {
          transform: translateY(-3px) scale(1.2);
          text-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }

        .logo-letter-glow {
          display: inline-block;
          transition: all 0.3s ease;
          text-shadow: 0 0 10px rgba(20, 184, 166, 0.3);
        }

        .logo-letter-glow:hover {
          transform: translateY(-3px) scale(1.2) rotate(5deg);
          text-shadow: 0 0 20px rgba(20, 184, 166, 0.8), 0 4px 8px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </>
  );
}
